import { last, first } from 'lodash';
import { findMarkWords } from '../../common/functions';
import checkst from '../assets/icons/checkst.png';
import newst from '../assets/icons/newst.png';

export const DOUBLE_HEIGHT_RATIO = 1.5;

export const drawMixin = {
  // ***************************************************************************************
  // drawCanvas()
  // permet de dessiner le canvas
  // ***************************************************************************************
  drawCanvas() {
    const { displayNumberOfLines, fontName, teletext } =
      this.props.subtitleParameters;
    const { marginTop, marginLeft, marginBottom } =
      this.props.displayParameters;
       
    this.fontSize =
      (this.height /
        (displayNumberOfLines -
          parseFloat(marginTop) / 100 -
          parseFloat(marginLeft) / 100)) *
      (teletext ? 0.8 : 1);

    this.doubleFontSize = this.fontSize * DOUBLE_HEIGHT_RATIO;

    this.backCtx = this.backCanvas.getContext('2d');

    // On clear les valeurs sur le backCanvas
    this.backCtx.clearRect(0, 0, this.width, this.height);
    this.backCtx.font = `${this.fontSize}px ${teletext ? 'Courier New' : fontName}`;

    const marginTopPx = this.height * (parseFloat(marginTop) / 100);
    const marginBottomPx = this.height * (parseFloat(marginBottom) / 100);

    const heightLessMargin = this.height - marginTopPx - marginBottomPx;
    const lineHeight = heightLessMargin / displayNumberOfLines;

    this.lineHeight = lineHeight;
  },

  drawCanvasWithDimension() {
    this.foreCtx = this.foreCanvas.current.getContext('2d');
    this.foreCtx.clearRect(0, 0, this.width, this.height);

    if (!this.props.videoDimensions ||
      Object.values(this.props.videoDimensions).every((val) => val === undefined)) {
      this.foreCtx.drawImage(this.backCanvas, 0, 0);
      return;
    }

    const { videostartx, videostarty, videowidthx, videoheighty } =
      this.props.videoDimensions;

    if (this.props.playerParams.dontscale) {
     
      const hauteurcvideo = videoheighty + 2 * videostarty;
      const largeurcvideo = videowidthx + 2 * videostartx;

      const decaley = (videostarty / hauteurcvideo) * this.height;
      const decalex = (videostartx / largeurcvideo) * this.width;

      // Dessiner avec les bonnes proportions
      this.foreCtx.drawImage(
        this.backCanvas,
        0,
        0,
        this.backCanvas.width,
        this.backCanvas.height,
        decalex,
        decaley,
        this.width - 2 * decalex,
        this.height - 2 * decaley
      );

    } else {
      this.foreCtx.drawImage(this.backCanvas, 0, 0, this.width, this.height);
    }
  },

  // ***************************************************************************************
  // createCursor()
  // permet de créer le curseur
  // ***************************************************************************************
  createCursor() {
    if (this.readOnly || !this.active || !this.subtitle) {
      return;
    }

    const { doubleHeight: spDoubleHeight, teletext, boxing, fontName } =
      this.props.subtitleParameters;

    let line = this.subtitle.lines[this.currentLineIndex];
    if (!line) {
      if (this.currentLineIndex === 0) {
        line = this.subtitle.lines[this.currentLineIndex + 1];
        this.currentLineIndex++;
      } else {
        line = this.subtitle.lines[this.currentLineIndex - 1];
        this.currentLineIndex--;
      }
    }

    if (!line) {
      if (!this.subtitle.lines.length) {
        return;
      }

      line = this.subtitle.lines[0];
      this.currentLineIndex = 0;
      this.currentWordIndex = 0;
    }

    const doubleHeight = line.doubleHeight ?? spDoubleHeight;
    const height = doubleHeight ? this.lineHeight * 2 : this.lineHeight;

    const heightY = doubleHeight
      ? this.lineHeight + this.lineHeight * 0.5
      : this.lineHeight;

    if (!line.words.length) {
      this.currentLine = line;
      this.currentWord = null;
      this.currentLetter = null;

      // dessiner le boxing
      if (teletext || boxing) {
        const y = doubleHeight ? line.startY + this.lineHeight : line.startY;

        this.drawBoxing(line.startX, y, this.spaceWidth, doubleHeight);
      }

      // dessiner le curseur
      const x = this.getCoordinateX(line);
      const y = doubleHeight
        ? line.startY + this.lineHeight - height
        : line.startY - height;
      
      // Dessiner le rectangle autour du mot
      this.drawRectangleWord(
        x,
        y,
        15,
        height,
        teletext || boxing ? 'black' : 'rgba(0, 0, 0, 0.15)',
        'transparent'
      );
      
      this.showCursor(x, y, height);
      return;
    }

    let word = line.words[this.currentWordIndex];
    if (!word) {
      word = line.words[0];
      this.currentWordIndex = 0;
    }

    let letter = word.letters
      ? word.letters.find((x) => x.indexLetter === this.currentLetterIndex)
      : null;

    this.currentLine = line;
    this.currentWord = word;
    this.currentLetter = letter;

    let x = 0; // Coordonnée X de la ligne

    if (!word.letters || word.letters.length === 0) {
      x = word.startX;
    } else {
      if (!letter) {
        letter = last(word.letters);
        if (!letter) {
          return;
        }

        this.currentLetter = letter;
        this.currentLetterIndex = letter.indexLetter;
      }

      x = letter.x;
    }

    let startY = word.startY - heightY;

    this.backCtx.font = `${
      doubleHeight ? this.doubleFontSize : this.fontSize
    }px ${teletext ? "Courier New" : fontName}`;

    // en cours de sélection
    if (this.underSelection) {
      return;
    }

    // Dessiner le rectangle autour du mot
    this.drawRectangleWord(
      word.startX,
      startY,
      word.width,
      height,
      teletext || boxing ? 'black' : 'rgba(0, 0, 0, 0.15)'
    );

    
    // Dessiner le mot recherché
    if (this.wordsToSearch && this.wordsToSearch.length) {
      const wordFounded =
        this.wordsToSearch[this.currentPositionSearch];
      
      if (wordFounded && wordFounded.id === this.subtitle.id) {
        const wordToSearch = wordFounded.words.find(
          (x) =>
            x.indexWord === word.indexWord
        );

         if (wordToSearch) {
          this.drawSearchWord(this.backCtx, 
            word, 
            doubleHeight,
            wordToSearch.startIndex, 
            wordToSearch.endIndex, 
            '#3E79BC');
         }

      }
    }

    // Redessiner le mot
    const stretch = doubleHeight ? 1 : DOUBLE_HEIGHT_RATIO;
    this.drawWord(word, stretch);

    // Dessiner le markword
    if (this.subtitle.mark && this.currentWord.indexWord === 0) {
      this.drawMarkWords(
        this.subtitle.mark,
        this.currentLine.startX,
        this.currentLine.startY
      );
    }

    // souligne le mot en erreur
    if (word.suggest) {
      this.drawSuggestErrors(this.backCtx, word);
    }

    // dessiner le curseur
    this.showCursor(x, startY, height);
  },

  // ***************************************************************************************
  // showCursor() permet d'afficher le curseur en continue (clignotant) ou non
  // ***************************************************************************************
  showCursor(x, y, height) {
    if (this.showCptCurs) {
      // Dessiner le curseur
      // En fonction du showCptCurs, on peut afficher le curseur de texte clignotant ou non
      this.cptCurs++;
      if (this.cptCurs < 50) {
        this.drawCursor(x, y, height);
      } else if (this.cptCurs > 100) this.cptCurs = 0;
    } else {
      this.drawCursor(x, y, height);
    }
  },

  // ***************************************************************************************
  // drawCursor()
  // Permet de dessiner le curseur
  // ***************************************************************************************
  drawCursor(x, y, height, color = 'red') {
    this.backCtx.beginPath();
    this.backCtx.moveTo(x, y); // Point de départ de la ligne
    this.backCtx.lineTo(x, y + height); // Point d'arrivée de la ligne
    this.backCtx.strokeStyle = color; // Couleur de la ligne
    this.backCtx.lineWidth = 2; // Épaisseur de la ligne
    this.backCtx.stroke();
  },

  // ***************************************************************************************
  // drawRectangleWord()
  // dessine un rectangle autour du mot (x, y, largeur, hauteur)
  // ***************************************************************************************
  drawRectangleWord(x, y, width, height, color, strokeColor = '#959595') {
    const rect = {
      x: x - 2,
      y,
      width: width - this.spaceWidth + 4,
      height,
    };
    this.backCtx.beginPath();

    // Couleur de remplissage du rectangle
    this.backCtx.fillStyle = color;
    this.backCtx.fillRect(rect.x, rect.y, rect.width, rect.height);

    // Ajouter une bordure autour du rectangle
    this.backCtx.strokeStyle = strokeColor; // Couleur de bordure
    this.backCtx.lineWidth = 2; // Largeur de la bordure
    this.backCtx.roundRect(rect.x, rect.y, rect.width, rect.height, [3]);
    this.backCtx.stroke();
  },

  // ***************************************************************************************
  // drawBoxing()
  // permet de dessiner le bandeau sur la phrase
  // ***************************************************************************************
  drawBoxing(startX, startY, textWidth, doubleHeight) {
    const { teletext, boxing } = this.props.subtitleParameters;
    const { background, backgroundtransparency  } = this.props.playerParams;

    if (!teletext && !boxing) {
      if (!background) {
        return;
      }
    }

    const rect = {
      x: startX - 10,
      y: startY - (doubleHeight ? this.lineHeight * 2 : this.lineHeight),
      width: textWidth + 20,
      height: doubleHeight ? this.lineHeight * 2 : this.lineHeight,
    };

    // Couleur de remplissage du rectangle
   this.backCtx.fillStyle = backgroundtransparency
        ? `rgba(0, 0, 0, ${backgroundtransparency / 100})`
        : 'black';
    this.backCtx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },

  // ***************************************************************************************
  // drawWord()
  // permet de dessiner le mot
  // ***************************************************************************************
  drawWord(word, stretch) {
    this.backCtx.fillStyle = word.color;
    this.backCtx.save();
    this.backCtx.translate(word.startX, word.startY);
    this.backCtx.scale(stretch ?? 1, 1);
    this.backCtx.fillText(word.text, 0, 0);
    this.backCtx.restore();
  },

  // ***************************************************************************************
  // strokeWord()
  // permet de dessiner la bordure du mot
  // ***************************************************************************************
  strokeWord(word, stretch) {
    this.backCtx.save();
    this.backCtx.translate(word.startX, word.startY);
    this.backCtx.scale(stretch ?? 1, 1);
    this.backCtx.strokeText(word.text, 0, 0);
    this.backCtx.restore();
  },

  // ***************************************************************************************
  // drawSelectionWord()
  // permet de dessiner la sélection des mots
  // ***************************************************************************************
  drawSelectionWord(x, y, width, height, word) {
    const rect = {
      x: x - 4,
      y,
      width: width + 2,
      height,
    };

    const indexSelection = this.wordsToSelect.findIndex(
      (x) => x.indexWord === word.indexWord
    );
    const prevWord = this.wordsToSelect[indexSelection - 1];
    const nextWord = this.wordsToSelect[indexSelection + 1];

    const isFirstSelection =
      indexSelection === 0 ||
      (prevWord && prevWord.indexLine !== word.indexLine);
    const isLastSelection =
      indexSelection === this.wordsToSelect.length - 1 ||
      (nextWord && nextWord.indexLine !== word.indexLine);

    if (isLastSelection) {
      rect.width -= this.spaceWidth - 4;
    }

    // Couleur de remplissage du rectangle
    this.backCtx.fillStyle = '#595959';

    const radius = 3;
    this.backCtx.beginPath();

    // Ajouter les coins arrondis en fonction de la sélection
    if (isFirstSelection && isLastSelection) {
      this.backCtx.roundRect(rect.x, rect.y, rect.width, rect.height, [radius]);
    } else if (isFirstSelection) {
      this.backCtx.roundRect(rect.x, rect.y, rect.width, rect.height, [
        radius,
        0,
        0,
        radius,
      ]);
    } else if (isLastSelection) {
      this.backCtx.roundRect(rect.x, rect.y, rect.width, rect.height, [
        0,
        radius,
        radius,
        0,
      ]);
    } else {
      this.backCtx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }

    this.backCtx.fill();
  },

  // ***************************************************************************************
  // drawSelectionLetter()
  // permet de dessiner la sélection des letters
  // ***************************************************************************************
  drawSelectionLetter(x, y, width, height) {
    const rect = {
      x: x - 2,
      y,
      width: width + 1,
      height,
    };

    // Couleur de remplissage du rectangle
    this.backCtx.fillStyle = '#595959';
    this.backCtx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },

  // ***************************************************************************************
  // drawMarkWords()
  // permet de marquer les mots dans le canvas
  // ***************************************************************************************
  drawMarkWords(mark, startX, startY) {
    const markWords = findMarkWords(mark);

    let iconX = startX;
    markWords.forEach((markWord) => {
      const icon = new Image();

      switch (markWord.key) {
        case 'CHECKWORD':
          icon.src = checkst;
          break;

        case 'NEWST':
          icon.src = newst;
          break;

        default:
          throw Error('icon not implemented');
      }

      const iconY = startY + this.lineHeight;
      this.backCtx.drawImage(icon, iconX, iconY, 12, 12);

      iconX += 12;
    });
  },

  // ***************************************************************************************
  // drawDelay() permet d'afficher le retard de sous-titre entre in du sous-titre et in du premier mot
  // ***************************************************************************************
  drawDelay() {
    const firstWord = first(this.getWords());

    if (!firstWord) {
      return;
    }

    const { doubleHeight, fontName, teletext } = this.props.subtitleParameters;

    const inSubtitle = this.subtitle.in;
    const delay = (firstWord.in - inSubtitle).toFixed(2);

    this.backCtx.font = `16px ${teletext ? 'Courier New' : fontName}`;
    const width = this.backCtx.measureText(delay.toString()).width;

    // dessiner le boxing
    const rect = {
      x: firstWord.startX - 10,
      y:
        firstWord.startY -
        (doubleHeight ? this.lineHeight * 2 : this.lineHeight + 10),
      width: width + 20,
      height: 20,
    };

    this.backCtx.fillStyle = 'black';
    this.backCtx.fillRect(rect.x, rect.y - 14, rect.width, rect.height);

    // dessiner le texte
    this.backCtx.fillStyle = 'white';
    this.backCtx.fillText(delay.toString(), rect.x + 5, rect.y);
  },

  // ***************************************************************************************
  // drawSuggestErrors() permet d'afficher le mot en erreur
  // ***************************************************************************************
  drawSuggestErrors(ctx, word) {
    if (!word.suggest) {
      return;
    }

    const startX = word.startX;
    let endX = word.endX - this.spaceWidth;
    const endY = word.endY - 15;

    ctx.beginPath();
    ctx.moveTo(startX, endY);
    ctx.lineTo(endX, endY);

    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    ctx.stroke();
  },

  // ***************************************************************************************
  // drawSearchWord()
  // permet de dessiner le mot recherché
  // ***************************************************************************************
  drawSearchWord(ctx, currentWord, doubleHeight, startIndex, endIndex, color = '#9F6D0A') {
    if (!currentWord || !currentWord.letters || !currentWord.letters.length || !currentWord.letters[startIndex]) return;

    const startX = currentWord.letters[startIndex].x;
    let width = 0;

    for (let i = startIndex; i < endIndex; i++) {
      if (currentWord.letters[i]) {
        width += currentWord.letters[i].width;
      }
    }

    const y = doubleHeight
      ? this.lineHeight + this.lineHeight * 0.5
      : this.lineHeight;
    const height = doubleHeight
      ? this.lineHeight * 2
      : this.lineHeight;

    const rect = {
      x: startX,
      y: currentWord.startY - y,
      width,
      height,
    };

    ctx.fillStyle = color;
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
  },
};
